<template>
  <div class="mc-video-history-new__grid-video-item-wrapper">
    <EndState v-if="state === 'ENDED'" @repeat="repeatVideo" @next="nextVideo" />
    <VideoNotStarted v-if="state === 'NOT_STARTED'" @play="playVideo" />
    <VideoNoVideo v-else-if="!state" :label="$t('trackVideo.state.noVideo')" />
    <VideoWebview
      v-else
      v-show="state !== 'ENDED'"
      ref="videoWebviewRef"
      :channel="channel"
      :state="state ?? ''"
      :imei="imei ?? ''"
      @toggleMuted="toggleMuted"
      @togglePaused="togglePaused"
      @toggleFillFit="toggleFillFit"
      @toggleFullscreen="toggleFullscreen"
    />
    <span class="mc-video-history-new__video-section-camera-label">
      {{ $t('trackVideo.camera') + ' ' + channel }}
    </span>
    <span class="mc-video-history-new__video-section-close" @click="closeVideo">
      <i class="mc-icon icon-outline_close"></i>
    </span>
  </div>
</template>
<script>
import VideoWebview from './VideoWebview.vue'
import VideoNotStarted from './NotStarted.vue'
import VideoNoVideo from './NoVideo.vue'
import EndState from './End.vue'
import { mapGetters, mapState } from 'vuex'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
moment.locale('id')

export default {
  components: {
    VideoWebview,
    VideoNotStarted,
    VideoNoVideo,
    EndState,
  },
  mixins: [Helper],
  props: {
    channel: {
      type: Number,
      required: true,
    },
    imei: {
      type: String,
      required: true,
    },
    state: {
      type: String,
      required: true,
    },
  },

  emits: ['clickPlay', 'togglePaused'],

  computed: {
    ...mapState('videoHistory', [
      'mapFilelist',
      'mapWebviewStatePerChannel',
      'mapWebviewBufferedTimePerChannel',
      'mapFilelistUnionPerChannel',
      'period',
      'videoChannelActive',
    ]),
    ...mapGetters('videoHistory', ['markerTimelineDate', 'deviceTimezone']),
  },
  watch: {},

  methods: {
    playVideo() {
      this.$emit('clickPlay')
    },
    closeVideo() {
      this.$store.dispatch('videoHistory/removeChannelToGridWebview', this.channel)
    },
    postMessageToIframe(type, imei, channel, payload) {
      this.$store.dispatch('videoHistory/postToStreamIframe', {
        imei: this.imei,
        channel: this.channel,
        payload: {
          type: type,
          data: {
            channel: this.channel,
            imei: this.imei,
            ...payload,
          },
        },
      })
    },
    repeatVideo() {
      this.postMessageToIframe('VIDEO_HISTORY_RESTART_VIDEO', this.imei, this.channel, {})
    },
    nextVideo() {
      const unionFilelist = this.mapFilelistUnionPerChannel[this.channel]

      const localDateCurrent = this.convertUtcToLocalDate(
        this.markerTimelineDate,
        this.deviceTimezone
      )

      const currentTimestamp = new Date(localDateCurrent).getTime()
      const thresholdMilliseconds = 2 * 1000

      const reachedEndIndex =
        Array.isArray(unionFilelist) && Number.isFinite(currentTimestamp)
          ? unionFilelist.findIndex(({ endTime }) => {
              const endTimestamp = new Date(endTime).getTime()

              if (!Number.isFinite(endTimestamp)) {
                return false
              }

              return Math.abs(currentTimestamp - endTimestamp) <= thresholdMilliseconds
            })
          : -1

      if (reachedEndIndex === -1) return

      const nextInterval = unionFilelist[reachedEndIndex + 1]

      if (!nextInterval) return

      const utcNextStartTime = this.getUtcDateFromLocalDate(
        nextInterval.startTime,
        this.deviceTimezone
      )

      const [periodStart, periodEnd] = this.period

      const startTimestamp = moment.utc(periodStart).valueOf()
      const endTimestamp = moment.utc(periodEnd).valueOf()
      const selectedTimestamp = moment.utc(utcNextStartTime).valueOf()
      const duration = endTimestamp - startTimestamp

      if (
        !Number.isFinite(startTimestamp) ||
        !Number.isFinite(endTimestamp) ||
        !Number.isFinite(selectedTimestamp) ||
        duration <= 0
      ) {
        return
      }

      const position = ((selectedTimestamp - startTimestamp) / duration) * 100

      this.$store.commit(
        'videoHistory/setMarkerPositionTimeline',
        Math.min(Math.max(position, 0), 100)
      )

      this.$store.commit('videoHistory/setHasIntentToTimeChange', true)
      this.$store.dispatch('videoHistory/playChannel', this.channel)
    },
    toggleMuted(isMuted) {
      this.postMessageToIframe('VIDEO_HISTORY_TOGGLE_MUTED', this.imei, this.channel, {
        isMuted: isMuted,
      })
    },
    togglePaused(isPaused) {
      this.$emit('togglePaused', isPaused)
    },

    postMessageTogglePausedToIframe(isPaused) {
      this.postMessageToIframe('VIDEO_HISTORY_TOGGLE_PAUSED', this.imei, this.channel, {
        isPaused: isPaused,
      })
    },

    setIsPaused(isPaused) {
      this.$refs.videoWebviewRef.setIsPaused(isPaused)
    },

    toggleFillFit(isFill) {
      this.postMessageToIframe('VIDEO_HISTORY_TOGGLE_FILL_FIT', this.imei, this.channel, {
        isFill: isFill,
      })
    },
    toggleFullscreen(isFullscreen) {
      this.postMessageToIframe('VIDEO_HISTORY_TOGGLE_FULLSCREEN', {
        isFullscreen: isFullscreen,
      })
    },
  },
}
</script>
